import { MetadataEntityResponse, MetadataEntityType } from "@api/apiClient";

// Types for dropdown options
export interface SystemOption {
  value: string;
  label: string;
}

export interface SubSystemOption {
  value: string;
  label: string;
  systemId: string;
}

export type LocationType = 'region' | 'country';
export interface LocationOption {
  type: LocationType;
  region: string;
  value: string;
  label: string;
  alpha3?: string; // Optional ISO Alpha-3 code
}

// Utility functions to transform API metadata entities into dropdown options
export const transformMetadataToSystemOptions = (metadataEntities: MetadataEntityResponse[]): SystemOption[] => {
  const allSystems = metadataEntities?.filter(entity => entity.type === MetadataEntityType.System)
    .map(system => ({
      value: system.metadataEntityId || "",
      label: system.name || ""
    })) || [];

  allSystems.sort((a, b) => a.label.localeCompare(b.label));

  // Add default option
  allSystems.unshift({ value: "", label: "-- Select System --" });

  return allSystems;
};

export const transformMetadataToSubSystemOptions = (metadataEntities: MetadataEntityResponse[]): SubSystemOption[] => {
  const allSubSystems: SubSystemOption[] = [];

  // Extract subsystems from each system's children
  metadataEntities?.forEach(system => {
    if (system.type === MetadataEntityType.System && system.children && system.children.length > 0) {
      system.children.forEach((subSystem: MetadataEntityResponse) => {
        if (subSystem.type === MetadataEntityType.Subsystem) {
          allSubSystems.push({
            value: subSystem.metadataEntityId || "",
            label: subSystem.name || "",
            systemId: system.metadataEntityId || ""
          });
        }
      });
    }
  });

  allSubSystems.sort((a, b) => a.label.localeCompare(b.label));

  // Add default option
  allSubSystems.unshift({ value: "", label: "-- Select SubSystem --", systemId: "" });

  return allSubSystems;
};

export const transformMetadataToLocationOptions = (metadataEntities: MetadataEntityResponse[]): LocationOption[] => {
  const allLocations: LocationOption[] = [];

  // Extract locations from each system's children
  metadataEntities?.forEach(location => {
    if (location.type === MetadataEntityType.Region && location.children && location.children.length > 0) {
      location.children.forEach((country: MetadataEntityResponse) => {
        if (country.type === MetadataEntityType.Country) {
          allLocations.push({
            type: 'country',
            region: location.name || "",
            value: country.metadataEntityId || "",
            label: country.name || "",
            alpha3: country.description || ""
          });
        }
      });
    }
  });

  allLocations.sort((a, b) => a.label.localeCompare(b.label));

  // Extract regions themselves as options, sorted alphabetically then add to the beginning of the list
  const regions: LocationOption[] = metadataEntities?.filter(entity => entity.type === MetadataEntityType.Region).map(region => ({
    type: 'region',
    region: region.name || "",
    value: region.metadataEntityId || "",
    label: region.name || "",
    alpha3: region.name || ""
  })) || [];

  regions.sort((a, b) => a.label.localeCompare(b.label));

  // TODO: this should be added to the DB
  regions.unshift({
    type: 'region',
    region: "global",
    value: "global",
    label: "Global",
    alpha3: "global"
  } as LocationOption);

  allLocations.unshift(...regions);

  return allLocations;
};

// Helper function to find entity by ID
export const findMetadataEntityById = (metadataEntities: MetadataEntityResponse[], id: string): MetadataEntityResponse | null => {
  for (const entity of metadataEntities || []) {
    if (entity.metadataEntityId === id) {
      return entity;
    }
    if (entity.children && entity.children.length > 0) {
      const found = findMetadataEntityById(entity.children, id);
      if (found) return found;
    }
  }
  return null;
};

// Create lookup maps for better performance
export const createSystemLookup = (metadataEntities: MetadataEntityResponse[]): Record<string, string> => {
  const systemOptions = transformMetadataToSystemOptions(metadataEntities);
  return systemOptions.reduce((acc, option) => {
    if (option.value) acc[option.value] = option.label;
    return acc;
  }, {} as Record<string, string>);
};

export const createSubSystemLookup = (metadataEntities: MetadataEntityResponse[]): Record<string, string> => {
  const subSystemOptions = transformMetadataToSubSystemOptions(metadataEntities);
  return subSystemOptions.reduce((acc, option) => {
    if (option.value) acc[option.value] = option.label;
    return acc;
  }, {} as Record<string, string>);
};

// Utility functions to get text by ID - these are what you requested!
export const getSystemNameById = (metadataEntities: MetadataEntityResponse[], systemId: string): string => {
  if (!systemId) return "";
  const systemLookup = createSystemLookup(metadataEntities);
  return systemLookup[systemId] || "";
};

export const getSubSystemNameById = (metadataEntities: MetadataEntityResponse[], subSystemId: string): string => {
  if (!subSystemId) return "";
  const subSystemLookup = createSubSystemLookup(metadataEntities);
  return subSystemLookup[subSystemId] || "";
};

// Get system entity by ID
export const getSystemById = (metadataEntities: MetadataEntityResponse[], systemId: string): MetadataEntityResponse | null => {
  if (!systemId) return null;
  return metadataEntities?.find(entity => entity.type === MetadataEntityType.System && entity.metadataEntityId === systemId) || null;
};

// Get subsystem entity by ID
export const getSubSystemById = (metadataEntities: MetadataEntityResponse[], subSystemId: string): MetadataEntityResponse | null => {
  if (!subSystemId) return null;
  return findMetadataEntityById(metadataEntities, subSystemId);
};

// Get all subsystems for a specific system
export const getSubSystemsForSystem = (metadataEntities: MetadataEntityResponse[], systemId: string): MetadataEntityResponse[] => {
  if (!systemId) return [];

  const system = getSystemById(metadataEntities, systemId);
  if (!system || !system.children) return [];

  return system.children.filter(child => child.type === MetadataEntityType.Subsystem);
};

// Get parent system for a subsystem
export const getParentSystemForSubSystem = (metadataEntities: MetadataEntityResponse[], subSystemId: string): MetadataEntityResponse | null => {
  if (!subSystemId) return null;

  const subSystem = getSubSystemById(metadataEntities, subSystemId);
  if (!subSystem || !subSystem.parentId) return null;

  return getSystemById(metadataEntities, subSystem.parentId);
};

// Advanced utility: Get full hierarchy path (System > Subsystem)
export const getHierarchyPath = (metadataEntities: MetadataEntityResponse[], systemId?: string, subSystemId?: string): string => {
  const parts: string[] = [];

  if (systemId) {
    const systemName = getSystemNameById(metadataEntities, systemId);
    if (systemName && systemName !== "-- Select System --") {
      parts.push(systemName);
    }
  }

  if (subSystemId) {
    const subSystemName = getSubSystemNameById(metadataEntities, subSystemId);
    if (subSystemName && subSystemName !== "-- Select SubSystem --") {
      parts.push(subSystemName);
    }
  }

  return parts.join(" > ");
};

// Utility class for easier usage (optional - for OOP approach)
export class MetadataEntityHelper {
  private metadataEntities: MetadataEntityResponse[];
  private systemLookup: Record<string, string>;
  private subSystemLookup: Record<string, string>;

  constructor(metadataEntities: MetadataEntityResponse[]) {
    this.metadataEntities = metadataEntities;
    this.systemLookup = createSystemLookup(metadataEntities);
    this.subSystemLookup = createSubSystemLookup(metadataEntities);
  }

  // Fast lookup methods (pre-computed)
  getSystemName(systemId: string): string {
    return this.systemLookup[systemId] || "";
  }

  getSubSystemName(subSystemId: string): string {
    return this.subSystemLookup[subSystemId] || "";
  }

  getHierarchyPath(systemId?: string, subSystemId?: string): string {
    return getHierarchyPath(this.metadataEntities, systemId, subSystemId);
  }

  // Get all available options
  getSystemOptions(): SystemOption[] {
    return transformMetadataToSystemOptions(this.metadataEntities);
  }

  getSubSystemOptions(): SubSystemOption[] {
    return transformMetadataToSubSystemOptions(this.metadataEntities);
  }

  getSubSystemOptionsForSystem(systemId: string): SubSystemOption[] {
    const allSubSystems = this.getSubSystemOptions();
    return allSubSystems.filter(sub => !systemId || sub.systemId === systemId || sub.value === "");
  }
}
